import { ODIN_LOGO_SWITCH_KEY } from "../../constants";
import EventHelper from "../../network/helpers/event/EventHelper";
import CMSHelper from "../cms/CMSHelper";
import CMSBadgeAssetCache from "../cms/CMSBadgeAssetCache";
import store from "../../../redux/store";
import { EventEditionDetails, ICMSApiResponse, IProduct, IPersonBadge, Nullable } from "../../types";
import { getStoredColocatedShowsForEvent } from "../../utils/colocatedShowsUtils";
import MagentoHelper from "../../network/helpers/magento/MagentoHelper";
import { BadgeImagesInfo } from "./BadgeImageHelper.types";

class BadgeImageHelper {
  shouldPrintBadgeLogo = () => {
    return localStorage.getItem(ODIN_LOGO_SWITCH_KEY) === "true";
  };

  private getProductBySku = (sku: string, gbsCode: string): Nullable<IProduct> => {
    const state = store.getState() as any;
    const products = [
      ...MagentoHelper.magentoProducts,
      ...(state.magento?.regProducts || []),
      ...(state.magento?.colocatedRegProducts || [])
    ] as IProduct[];
    const candidates = products.filter(product => product.sku === sku);
    const normalizeGbs = (value: string) => value.trim().toUpperCase();
    return (
      candidates.find(product => normalizeGbs(product.rx_event_edition_code || "") === normalizeGbs(gbsCode)) ||
      candidates.find(product => !product.rx_event_edition_code) ||
      null
    );
  };

  private getImage = async (sku: string, imageName: string, eventEdition: EventEditionDetails, cms: ICMSApiResponse) => {
    const productUrl = CMSHelper.getCmsImageUrl(sku, imageName, eventEdition, cms);
    const productImage = () => CMSBadgeAssetCache.getValue(productUrl);
    if (this.getProductBySku(sku, eventEdition.gbsCode)?.rx_shared_logo_usage !== 1) return productImage();

    const sharedUrl = CMSHelper.getCmsImageUrl(sku, imageName, eventEdition, cms, {
      useSharedImages: true,
      useSharedLogo: imageName === "logo"
    });
    const sharedImage = await CMSBadgeAssetCache.load(sharedUrl);
    return sharedImage.available ? sharedImage.value : productImage();
  };

  isUrlAvailable = async (url: string): Promise<boolean> => {
    if (!url || !/^https?:\/\//i.test(url)) {
      return true;
    }

    const cached = CMSBadgeAssetCache.get(url);
    if (cached) return cached.available;

    return (await CMSBadgeAssetCache.load(url)).available;
  };

  private resolveEventEditionForBadge = async (badge: IPersonBadge, isFreya = false) => {
    const normalizeGbs = (value?: string | null) => (value || "").trim().toUpperCase();
    const currentEventEdition = EventHelper.eventEdition;
    const badgeGbsCode = badge.eventEditionGbsCode;
    const normalizedBadgeGbsCode = normalizeGbs(badgeGbsCode);

    if (normalizedBadgeGbsCode && normalizeGbs(currentEventEdition?.gbsCode) === normalizedBadgeGbsCode) {
      return currentEventEdition;
    }

    if (normalizedBadgeGbsCode && isFreya) {
      const stored = await getStoredColocatedShowsForEvent(currentEventEdition);
      const match = stored.find(show => normalizeGbs(show.gbsCode) === normalizedBadgeGbsCode);
      if (match) {
        return match;
      }
    }

    return currentEventEdition || null;
  };

  getBadgeLogo = async (sku: string) => {
    if (!this.shouldPrintBadgeLogo()) {
      return "";
    }
    const cmsEventDetails = store.getState().eventEdition.cmsEventDetails;
    if (!cmsEventDetails || !EventHelper.eventEdition) {
      return "";
    }
    return this.getImage(sku, "logo", EventHelper.eventEdition, cmsEventDetails);
  };

  getBadgeSideImages = async (sku: string) => {
    if (!this.shouldPrintBadgeLogo()) {
      return ["", "", ""];
    }

    const cmsEventDetails = store.getState().eventEdition.cmsEventDetails;
    if (!cmsEventDetails || !EventHelper.eventEdition) {
      return ["", "", ""];
    }

    const eventEdition = EventHelper.eventEdition;
    return Promise.all(["image1", "image2", "image3"].map(name => this.getImage(sku, name, eventEdition, cmsEventDetails)));
  };

  getBadgeLogoForBadge = async (sku: string, badge: IPersonBadge, isFreya = false) => {
    if (!this.shouldPrintBadgeLogo()) {
      return "";
    }

    const eventEdition = await this.resolveEventEditionForBadge(badge, isFreya);
    if (!eventEdition) {
      return "";
    }

    const cmsEventDetails = await CMSHelper.getCachedCmsEventDetails(eventEdition);
    if (!cmsEventDetails) {
      return "";
    }

    return this.getImage(sku, "logo", eventEdition, cmsEventDetails);
  };

  getBadgeSideImagesForBadge = async (sku: string, badge: IPersonBadge, isFreya = false) => {
    if (!this.shouldPrintBadgeLogo()) {
      return ["", "", ""];
    }

    const eventEdition = await this.resolveEventEditionForBadge(badge, isFreya);
    if (!eventEdition) {
      return ["", "", ""];
    }

    const cmsEventDetails = await CMSHelper.getCachedCmsEventDetails(eventEdition);
    if (!cmsEventDetails) {
      return ["", "", ""];
    }
    return Promise.all(["image1", "image2", "image3"].map(name => this.getImage(sku, name, eventEdition, cmsEventDetails)));
  };

  getBadgeImagesInfo = async (sku: string, badge: IPersonBadge, isFreya = false): Promise<BadgeImagesInfo> => {
    const [logoUrl, sideUrls] = await Promise.all([
      this.getBadgeLogoForBadge(sku, badge, isFreya),
      this.getBadgeSideImagesForBadge(sku, badge, isFreya)
    ]);
    const logo = logoUrl || "";
    const [image1 = "", image2 = "", image3 = ""] = sideUrls;
    const resolveImage = async (url: string) => {
      const available = url ? await this.isUrlAvailable(url) : undefined;
      return { value: available ? await CMSBadgeAssetCache.getValue(url) : url, available };
    };
    const images = await Promise.all([logo, image1, image2, image3].map(resolveImage));
    return {
      urls: { logo: images[0]!.value, image1: images[1]!.value, image2: images[2]!.value, image3: images[3]!.value },
      availability: {
        logo: images[0]!.available,
        image1: images[1]!.available,
        image2: images[2]!.available,
        image3: images[3]!.available
      }
    };
  };
}

const badgeImageHelper = new BadgeImageHelper();

export default badgeImageHelper;
